import { unwrapApiResponse } from "../utils/apiUtils";
import { api } from "./apiSlice";
import { EmployeeBirthdayDto } from "../types/employee";

// Truck Driver DTO
export interface TruckDriverDto {
  id: number;
  userId: number;
  employeeDriverInfoId: number | null;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string | null;
  isActive: boolean;
}

// Employee DTO (for all employees)
export interface EmployeeDto {
  id: number;
  userId: number;
  firstName: string;
  lastName: string;
  email: string;
  isActive: boolean;
  jobCategory: number; // JobCategory enum from backend
  worksWeekends: boolean;
}

// Dispatcher DTO
export interface DispatcherDto {
  id: number;
  userId: number;
  firstName: string;
  lastName: string;
  email: string;
  isActive: boolean;
}

// Employee Profile DTO
export interface UpdateEmployeeProfileDto {
  dateOfBirth?: string;
  familyHoliday?: string;
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  phoneNumber?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
}

// Extend the existing API slice with employee endpoints
export const employeesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Get all employees
    getEmployees: builder.query<EmployeeDto[], void>({
      query: () => "employees",
      transformResponse: unwrapApiResponse,
      providesTags: ["TruckAccounts"],
    }),

    // Get all truck drivers
    getTruckDrivers: builder.query<TruckDriverDto[], void>({
      query: () => "employees/drivers",
      transformResponse: unwrapApiResponse,
      providesTags: ["TruckDrivers"],
    }),

    // Get a truck driver by EmployeeDriverInfo ID
    getEmployeeByDriverInfoId: builder.query<TruckDriverDto, number>({
      query: (employeeDriverInfoId) => `employees/driver-info/${employeeDriverInfoId}`,
      transformResponse: unwrapApiResponse,
      providesTags: (result, error, employeeDriverInfoId) => [
        { type: "TruckDrivers", id: employeeDriverInfoId },
      ],
    }),

    // Get all dispatchers
    getDispatchers: builder.query<DispatcherDto[], void>({
      query: () => "employees/dispatchers",
      transformResponse: unwrapApiResponse,
      providesTags: ["TruckAccounts"],
    }),

    // Get employee profile
    getEmployeeProfile: builder.query<UpdateEmployeeProfileDto, number>({
      query: (userId) => `employees/${userId}/profile`,
      providesTags: (result, error, userId) => [
        { type: "User", id: userId },
      ],
      transformResponse: unwrapApiResponse,
    }),

    // Update employee profile
    updateEmployeeProfile: builder.mutation<boolean, { userId: number; profile: UpdateEmployeeProfileDto }>({
      query: ({ userId, profile }) => ({
        url: `employees/${userId}/profile`,
        method: "PUT",
        body: profile,
      }),
      transformResponse: unwrapApiResponse,
      invalidatesTags: ["User"], // Invalidate users cache to refresh profile data
    }),

    // Get employee birthdays for month (Admin)
    getEmployeeBirthdaysForMonth: builder.query<
      EmployeeBirthdayDto[],
      { year: number; month: number }
    >({
      query: ({ year, month }) => ({
        url: "employees/birthdays",
        params: { year, month },
      }),
      transformResponse: unwrapApiResponse,
      providesTags: ["TruckAccounts"],
    }),
  }),
});

// Export hooks for use in components
export const {
  useGetEmployeesQuery,
  useGetTruckDriversQuery,
  useGetEmployeeByDriverInfoIdQuery,
  useGetDispatchersQuery,
  useGetEmployeeProfileQuery,
  useUpdateEmployeeProfileMutation,
  useGetEmployeeBirthdaysForMonthQuery,
} = employeesApi;
